// frontend/src/hooks/games/useSudokuGame.ts

import { useCallback, useEffect, useMemo, useState } from 'react';
import { generateSudokuPuzzle, SudokuGrid } from '../../components/games/sudoku/sudokuGenerator';
import { SUDOKU_DIFFICULTIES, SudokuDifficulty } from '../../components/games/sudoku/sudokuConstants';

export interface SudokuCell {
  value: number; // 0 = boş
  isFixed: boolean; // başlangıç ipucu mu
  isError: boolean; // girilen değer çözümle uyuşmuyor mu
}

export interface CellPosition {
  row: number;
  col: number;
}

function buildCells(puzzle: SudokuGrid): SudokuCell[][] {
  return puzzle.map((row) =>
    row.map((value) => ({ value, isFixed: value !== 0, isError: false }))
  );
}

export function useSudokuGame(initialDifficulty: SudokuDifficulty = 'easy') {
  const [difficulty, setDifficulty] = useState<SudokuDifficulty>(initialDifficulty);
  const [{ puzzle, solution }, setPuzzleData] = useState(() =>
    generateSudokuPuzzle(SUDOKU_DIFFICULTIES[initialDifficulty].clues)
  );
  const [cells, setCells] = useState<SudokuCell[][]>(() => buildCells(puzzle));
  const [selected, setSelected] = useState<CellPosition | null>(null);
  const [mistakes, setMistakes] = useState(0);

  const isComplete = useMemo(
    () => cells.every((row) => row.every((cell) => cell.value !== 0 && !cell.isError)),
    [cells]
  );

  const startNewGame = useCallback((nextDifficulty?: SudokuDifficulty) => {
    const targetDifficulty = nextDifficulty ?? difficulty;
    const generated = generateSudokuPuzzle(SUDOKU_DIFFICULTIES[targetDifficulty].clues);
    setDifficulty(targetDifficulty);
    setPuzzleData(generated);
    setCells(buildCells(generated.puzzle));
    setSelected(null);
    setMistakes(0);
    // NOT: İleride buraya round başlangıcı için XP/istatistik event'i eklenebilir.
  }, [difficulty]);

  const selectCell = useCallback((row: number, col: number) => {
    setSelected({ row, col });
  }, []);

  const inputNumber = useCallback((digit: number) => {
    if (!selected) return;
    const { row, col } = selected;
    setCells((prev) => {
      const target = prev[row][col];
      if (target.isFixed) return prev;
      const isError = digit !== solution[row][col];
      if (isError) setMistakes((m) => m + 1);
      const next = prev.map((r) => [...r]);
      next[row][col] = { value: digit, isFixed: false, isError };
      return next;
    });
  }, [selected, solution]);

  const eraseCell = useCallback(() => {
    if (!selected) return;
    const { row, col } = selected;
    setCells((prev) => {
      const target = prev[row][col];
      if (target.isFixed) return prev;
      const next = prev.map((r) => [...r]);
      next[row][col] = { value: 0, isFixed: false, isError: false };
      return next;
    });
  }, [selected]);

  // NOT: İleride bulmaca tamamlandığında (isComplete true) burada
  // XP/başarım/istatistik event'i tetiklenebilir. Şu an için implemente edilmedi.

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key >= '1' && e.key <= '9') {
        inputNumber(Number(e.key));
        return;
      }
      if (e.key === 'Backspace' || e.key === 'Delete') {
        eraseCell();
        return;
      }
      setSelected((current) => {
        if (!current) return current;
        const { row, col } = current;
        if (e.key === 'ArrowUp') return { row: Math.max(0, row - 1), col };
        if (e.key === 'ArrowDown') return { row: Math.min(8, row + 1), col };
        if (e.key === 'ArrowLeft') return { row, col: Math.max(0, col - 1) };
        if (e.key === 'ArrowRight') return { row, col: Math.min(8, col + 1) };
        return current;
      });
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [inputNumber, eraseCell]);

  return {
    difficulty,
    cells,
    selected,
    mistakes,
    isComplete,
    selectCell,
    inputNumber,
    eraseCell,
    startNewGame,
  };
}
